import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import {
  collection,
  getDocs,
  serverTimestamp,
  doc,
  setDoc,
  updateDoc,
  addDoc,
} from "firebase/firestore";
import { db } from "../firebaseConfig";
import { v4 as uuidv4 } from "uuid";
import Nav from "./Nav";
import Sidebar from "./Sidebar";
import TablaOrdenes from "./TablaOrdenes";
import DatePicker, { registerLocale } from "react-datepicker";
import es from "date-fns/locale/es";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import { useNavContext } from "../context/NavContext";
import { useNumeroConfig } from "../hooks/useNumeroConfig";
import useArchivos from "../hooks/useArchivos";
import useProductos from "../hooks/useProductos";

moment.locale("es");
registerLocale("es", es);

function MiniSelect({ value, onChange, options, width = 64, height = 40 }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  // Cierra el menú si haces clic fuera
  React.useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div
      ref={ref}
      style={{
        position: "relative",
        width,
        height,
        display: "inline-block",
      }}
    >
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
        style={{
          width: "100%",
          height: "100%",
          border: "none",
          borderLeft: "1px solid #ddd",
          background: "#fff",
          fontSize: 14,
          fontWeight: 500,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderTopRightRadius: 8,
          borderBottomRightRadius: 8,
          boxShadow: "inset 0 0 0 1px #ddd",
          color: "#111",
          padding: 0,
        }}
      >
        <span style={{ position: "relative", top: "1px" }}>{value}</span>
        <i
          className="bi bi-caret-down-fill"
          style={{
            marginLeft: 6,
            fontSize: 12,
            color: "#666",
            position: "relative",
            top: "1px",
          }}
        />
      </button>

      {open && (
        <div
          role="listbox"
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            right: 0,
            zIndex: 2000,
            minWidth: "100%",
            background: "#fff",
            border: "1px solid #e7e7ea",
            borderRadius: 8,
            boxShadow: "0 8px 24px rgba(0,0,0,.12)",
            padding: 4,
          }}
        >
          {options.map((opt) => {
            const selected = value === opt.value;
            return (
              <div
                key={opt.value}
                role="option"
                aria-selected={selected}
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
                style={{
                  padding: "8px 12px",
                  borderRadius: 8,
                  fontSize: 14,
                  textAlign: "center",
                  cursor: "pointer",
                  background: selected ? "#eaf1ff" : "transparent",
                  color: selected ? "#0a66ff" : "#111",
                  transition: "background 0.15s",
                }}
              >
                {opt.label}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function OrdenForm(props) {
  const { orden, closeModal } = props;
  const [productos, setProductos] = useState([]);
  const {
    numero: numeroOrdenAuto,
    modo: modoNumeroOrden,
    abrirConfiguracion,
    NumeroConfigModalUI,
  } = useNumeroConfig("numeroOrden", { title: "Configurar número de orden" });
  const {
    archivos,
    initArchivos,
    cargando,
    manejarArchivos,
    eliminarArchivo,
    descargarArchivo,
    obtenerIcono,
  } = useArchivos("ordenes");
  const {
    datos,
    descuento,
    añadirFila,
    eliminar,
    actualizarCantidad,
    handleInputChange,
    filteredProductos,
    activeRow,
    subtotalConDesc,
    iva,
    total,
    obtenerIVA,
    calcularSubtotal,
    setDatos,
    setFilteredProductos,
    agregarBulk,
    setDescuento,
  } = useProductos(
    orden?.productosSeleccionados || [],
    orden?.descuento || 0,
    productos
  );
  const [numeroSobrescrito, setNumeroSobrescrito] = useState(null);
  const [comprador, setComprador] = useState("");
  const [referencia, setReferencia] = useState("");
  const [incoterm, setInconterm] = useState("");
  const [divisa, setDivisa] = useState("MXN");
  const [condicion, setCondicion] = useState("");
  const [cotizacion, setCotizacion] = useState("");
  const [proveedor, setProveedor] = useState("");
  const [proveedores, setProveedores] = useState([]);
  const [fechaVencimiento, setFechaVencimiento] = useState("");
  const [mostrarTabla] = useState(false);
  const [estado, setEstado] = useState("Pendiente");
  const [ordenes, setOrdenes] = useState([]);
  const [formDirty, setFormDirty] = useState(false);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [fechaOrden, setFechaOrden] = useState(new Date());
  const [notas, setNotas] = useState("");
  const [notasFocus, setNotasFocus] = useState(false);
  const { proximasAVencer, proximosEventos } = useNavContext();
  const [tipoDescuentoGlobal, setTipoDescuentoGlobal] = useState("%");

  useEffect(() => {
    if (orden) {
      setNumeroSobrescrito(orden.numeroOrden);
      setModoEdicion(true);

      // 1) Inicializamos el hook de productos con las filas de la orden
      setDatos(
        (orden.productosSeleccionados || []).map((p) => ({
          id: uuidv4(), // ID nuevo para la UI
          ...p,
          numeroDeParteCliente: p.numeroDeParteCliente || "",
          nombre: p.nombre || "",
          cantidad: p.cantidad ?? 0,
          tarifa: p.tarifa ?? 0,
          iva: p.iva ?? 0,
          descuento: p.descuento ?? 0,
          tipoDescuento: p.tipoDescuento || "%",
          importe: p.importe ?? 0,
        }))
      );

      // 2) Inicializamos el descuento global en el hook
      setDescuento(parseFloat(orden.descuento) || 0);

      // 3) Inicializamos el hook de archivos con los archivos de la orden
      initArchivos(orden.archivos || []);

      // 4) Resto de campos del formulario
      setFechaOrden(orden.fechaOrden ? new Date(orden.fechaOrden) : new Date());
      setFechaVencimiento(
        orden.fechaVencimiento ? new Date(orden.fechaVencimiento) : null
      );
      setProveedor(orden.proveedor);
      setComprador(orden.comprador);
      setReferencia(orden.referencia);
      setCotizacion(orden.cotizacion);
      setEstado(orden.estado || "Pendiente");
      setInconterm(orden.incoterm || "");
      setNotas(orden.notas || "");
      setDivisa(orden.divisa || "MXN");
      setCondicion(orden.condicion || "");
    } else {
      // Nuevo formulario: modo alta
      setNumeroSobrescrito(null);

      // 1) Arrancamos el hook de productos con UNA fila vacía
      setDatos([
        {
          id: uuidv4(),
          nombre: "",
          numeroDeParteCliente: "",
          cantidad: 0,
          tarifa: 0,
          iva: 0,
          descuento: 0,
          tipoDescuento: "%",
          importe: 0,
        },
      ]);

      // 2) Descuento en 0
      setDescuento(0);

      // 3) Inicializamos el hook de archivos vacío
      initArchivos([]);

      // 4) Resto de campos en blanco o por defecto
      setFechaOrden(new Date());
      setFechaVencimiento(null);
      setProveedor("");
      setComprador("");
      setReferencia("");
      setCotizacion("");
      setEstado("Pendiente");
      setInconterm("");
      setNotas("");
      setDivisa("MXN");
      setCondicion("");
    }
  }, [orden, setDatos, setDescuento, initArchivos]);

  const handleCancel = () => {
    if (formDirty) {
      Swal.fire({
        title: "¿Seguro que quieres salir?",
        text: "Se perderán los cambios realizados",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Sí, salir",
        cancelButtonText: "Cancelar",
      }).then((result) => {
        if (result.isConfirmed) {
          // Cerrar el modal usando la función pasada como prop
          closeModal();
          setFormDirty(false); // Opcional: resetea el estado si es necesario
        }
      });
    } else {
      // Si no hay cambios, simplemente cierra el modal
      closeModal();
    }
  };

  useEffect(() => {
    async function fetchData() {
      try {
        // Lanzamos ambas consultas en paralelo
        const [provSnap, prodSnap] = await Promise.all([
          getDocs(collection(db, "proveedores")),
          getDocs(collection(db, "productos")),
        ]);

        // Procesamos proveedores
        let proveedoresList = provSnap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          // Ordenamos
          .sort((a, b) =>
            a.empresa.localeCompare(b.empresa, "es", { sensitivity: "base" })
          );

        // Eliminamos duplicados por empresa
        proveedoresList = proveedoresList.filter(
          (prov, idx, arr) =>
            arr.findIndex((x) => x.empresa === prov.empresa) === idx
        );

        setProveedores(proveedoresList);

        // Procesamos productos
        const productosList = prodSnap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
          tipoDescuento: "cantidad",
          valorDescuento: 0,
        }));
        setProductos(productosList);
      } catch (error) {
        console.error("Error al cargar proveedores o productos:", error);
      }
    }

    fetchData();
  }, []);

  const guardar = async (e) => {
    e.preventDefault();

    // 1) Validación básica
    if (
      !proveedor ||
      !(numeroSobrescrito ?? numeroOrdenAuto) ||
      !fechaOrden ||
      !comprador
    ) {
      return Swal.fire({
        icon: "error",
        title: "Campos obligatorios faltantes",
        text: "Por favor completa los campos: Proveedor, Número de Orden, Fecha de Orden y Comprador.",
      });
    }

    // 2) Número de orden efectivo
    const numeroEfectivo = numeroSobrescrito ?? numeroOrdenAuto;

    try {
      // 3) Verificar duplicados
      const snapshot = await getDocs(collection(db, "ordenes"));
      const dup = snapshot.docs.find(
        (d) => d.data().numeroOrden === numeroEfectivo
      );
      if (dup && (!modoEdicion || (orden?.id && orden.id !== dup.id))) {
        return Swal.fire({
          icon: "error",
          title: "Número de orden duplicado",
          text: `El número "${numeroEfectivo}" ya existe. Usa otro.`,
        });
      }

      // 4) Nombre del proveedor
      const sel = proveedores.find((p) => p.id === proveedor);
      const nombreProveedor = sel?.empresa || "";

      // 5) Archivos: ya tienes URLs en `archivos`
      const archivosURLs = archivos.map(({ nombre, url }) => ({ nombre, url }));

      // 6) Preparo objeto con TODO el estado
      const ordenData = {
        proveedor,
        nombreProveedor,
        numeroOrden: numeroEfectivo,
        comprador,
        referencia,
        fechaOrden: fechaOrden.toISOString(),
        fechaVencimiento: fechaVencimiento?.toISOString() || null,
        estado,
        incoterm,
        divisa,
        condicion,
        cotizacion,
        notas,
        productosSeleccionados: datos,
        total,
        descuento,
        // Solo pongo createdAt en creación
        ...(modoEdicion
          ? { updatedAt: serverTimestamp() }
          : { createdAt: serverTimestamp() }),
        archivos: archivosURLs,
      };

      // 7) CREATE o UPDATE
      if (modoEdicion && orden?.id) {
        // Actualizar existente
        await updateDoc(doc(db, "ordenes", orden.id), ordenData);
        // Reflejar en lista padre
        setOrdenes?.((prev) =>
          prev.map((o) =>
            o.id === orden.id ? { id: orden.id, ...ordenData } : o
          )
        );
        await Swal.fire({
          icon: "success",
          title: "Orden actualizada",
          showConfirmButton: false,
          timer: 1500,
        });
      } else {
        // Crear nueva
        const docRef = await addDoc(collection(db, "ordenes"), ordenData);

        setNumeroSobrescrito(null);

        // Auto-incremento si está en automático
        if (modoNumeroOrden === "auto") {
          const m = numeroEfectivo.match(/^(.+?)(\d+)$/);
          if (m) {
            const [, pref, num] = m;
            const next = Number(num) + 1;
            await setDoc(
              doc(db, "configuracion", "numeroOrden"),
              { modo: "auto", prefijo: pref, siguienteNumero: next },
              { merge: true }
            );
          }
        }

        // Agregar a lista padre
        setOrdenes?.((prev) => [...prev, { id: docRef.id, ...ordenData }]);
        await Swal.fire({
          icon: "success",
          title: "Orden guardada",
          showConfirmButton: false,
          timer: 1500,
        });
      }

      // 8) Reset dirty + cerrar modal (sin recargar)
      setFormDirty(false);
      closeModal();
    } catch (err) {
      console.error(err);
      Swal.fire({
        icon: "error",
        title: "Error al guardar la orden",
        text: err.message,
      });
    }
  };

  // añade esto dentro del componente OrdenForm, antes del return
  const handleEliminarAdjunto = async (nombre) => {
    try {
      // 1) elimina del storage/estado (hook)
      await eliminarArchivo(nombre);

      // 2) si estás editando una orden existente, persiste el cambio en Firestore
      if (modoEdicion && orden?.id) {
        const restantes = (archivos || [])
          .filter((a) => a.nombre !== nombre)
          .map(({ nombre, url }) => ({ nombre, url }));

        await updateDoc(doc(db, "ordenes", orden.id), {
          archivos: restantes,
          updatedAt: serverTimestamp(),
        });

        // 3) opcional: refleja el cambio en la lista padre si la tienes montada
        setOrdenes?.((prev) =>
          prev.map((o) =>
            o.id === orden.id ? { ...o, archivos: restantes } : o
          )
        );
      }
    } catch (e) {
      console.error("Error eliminando adjunto:", e);
      Swal.fire({
        icon: "error",
        title: "No se pudo eliminar el archivo",
        text: e.message,
      });
    }
  };

  return (
    <>
      <Nav
        proximasAVencer={proximasAVencer}
        proximosEventos={proximosEventos}
      />

      <Sidebar />

      <div className="cotizacion-form">
        <form
          onKeyDown={(e) => {
            // Solo bloquear "Enter" fuera de los elementos <textarea>
            if (e.key === "Enter" && e.target.tagName !== "TEXTAREA") {
              e.preventDefault();
            }
          }}
        >
          <div>
            <div className="group apartado">
              <label htmlFor="proveedor" className="required">
                Proveedor*
              </label>
              <select
                id="proveedor"
                name="proveedor"
                value={proveedor}
                onChange={(e) => {
                  setProveedor(e.target.value);
                  setFormDirty(true); // Marcar que el formulario ha sido modificado
                }}
              >
                <option value="">Seleccionar Proveedor</option>
                {proveedores.map((proveedor) => (
                  <option key={proveedor.id} value={proveedor.id}>
                    {proveedor.empresa}
                  </option>
                ))}
              </select>
            </div>

            <br />

            <div className="group">
              <label htmlFor="numeroOrden" className="required">
                No. Orden#*
              </label>
              <div className="input-wrapper">
                <input
                  type="text"
                  id="numeroOrden"
                  name="numeroOrden"
                  // Si el usuario sobreescribe manualmente, usamos ese valor,
                  // si no, el automático que viene del hook.
                  value={numeroSobrescrito ?? numeroOrdenAuto}
                  onChange={(e) => {
                    if (modoNumeroOrden === "manual") {
                      const v = e.target.value.trim();
                      // Si dejan en blanco, volvemos a null para caer en auto
                      setNumeroSobrescrito(v === "" ? null : v);
                    }
                  }}
                  // Deshabilitado en modo automático
                  disabled={modoNumeroOrden === "auto"}
                />
                <button
                  type="button"
                  className="icon-button"
                  // Abrimos el modal de configuración proveniente del hook
                  onClick={abrirConfiguracion}
                >
                  <i className="bi bi-gear-fill"></i>
                </button>
              </div>
            </div>

            <div className="group">
              <label htmlFor="comprador" className="required">
                Comprador#*
              </label>
              <input
                type="text"
                id="comprador"
                name="comprador"
                value={comprador}
                onChange={(e) => {
                  setComprador(e.target.value);
                  setFormDirty(true); // Marcar que el formulario ha sido modificado
                }}
              />
            </div>

            <div className="group fechas">
              <div className="group">
                <label htmlFor="fecha-orden" className="required">
                  Fecha de orden*
                </label>
                <DatePicker
                  selected={fechaOrden}
                  onChange={(date) => {
                    setFechaOrden(date);
                    setFormDirty(true); // Marcar el formulario como modificado
                  }}
                  dateFormat="dd MMM yyyy"
                  placeholderText="dd MMM yyyy"
                  id="fecha-cotizacion"
                  locale="es"
                />
              </div>
              <div className="group">
                <label htmlFor="fecha-vencimiento">Fecha de vencimiento</label>
                <div>
                  <DatePicker
                    selected={fechaVencimiento}
                    onChange={(date) => {
                      setFechaVencimiento(date);
                      setFormDirty(true); // Marcar el formulario como modificado
                    }}
                    dateFormat="dd MMM yyyy"
                    placeholderText="dd MMM yyyy"
                    id="fecha-vencimiento"
                    locale="es"
                  />
                </div>
              </div>
            </div>

            <div className="group">
              <label htmlFor="referencia">Referencia:</label>
              <input
                type="text"
                id="referencia"
                name="referencia"
                value={referencia}
                onChange={(e) => {
                  setReferencia(e.target.value);
                  setFormDirty(true); // Marcar que el formulario ha sido modificado
                }}
              />
            </div>

            <hr
              style={{
                marginBottom: "30px",
                marginTop: "20px",
                border: "1px solid #f9f9fb",
              }}
            />

            <div className="group fechas">
              <div className="group">
                <label htmlFor="incoterm">Incoterms:</label>
                <select
                  id="incoterm"
                  name="incoterm"
                  value={incoterm}
                  onChange={(e) => setInconterm(e.target.value)}
                  style={{ width: "42%" }}
                >
                  <option value="ninguno">Ninguno</option>
                  <option value="exw">EXW</option>
                  <option value="fob">FOB</option>
                  <option value="cif">CIF</option>
                  <option value="dap">DAP</option>
                  <option value="ddp">DDP</option>
                </select>
              </div>

              <div className="group">
                <label htmlFor="divisa">Divisa</label>
                <select
                  id="divisa"
                  name="divisa"
                  value={divisa}
                  onChange={(e) => {
                    setDivisa(e.target.value);
                    setFormDirty(true); // Marcar el formulario como modificado
                  }}
                  style={{ width: "41%" }}
                >
                  <option value="MXN">MXN</option>
                  <option value="USD">USD</option>
                </select>
              </div>
            </div>

            <div className="group fechas">
              <div className="group">
                <label htmlFor="condicion">Condición de Pago:</label>
                <input
                  type="text"
                  id="condicion"
                  name="condicion"
                  value={condicion}
                  onChange={(e) => setCondicion(e.target.value)}
                />
              </div>

              <div className="group">
                <label htmlFor="cotizacion">Cotización:</label>
                <input
                  style={{ width: "39%" }}
                  type="text"
                  id="cotizacion"
                  name="cotizacion"
                  value={cotizacion}
                  onChange={(e) => setCotizacion(e.target.value)}
                />
              </div>
            </div>

            <br />

            <div className="apartado2">Tabla de productos</div>

            <div>
              {/* Contenedor para la tabla y los botones */}
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  width: "100%",
                }}
              >
                {/* Tabla general */}

                <div>
                  <table className="productos-table">
                    <thead>
                      <tr>
                        <th>Detalles del Artículo</th>
                        <th style={{ textAlign: "center" }}>Modelo</th>
                        <th id="cantidad-col">Cantidad</th>
                        <th id="precio-col">Precio</th>
                        <th id="descuento-col">Descuento</th>
                        <th id="importe-col">Importe</th>
                      </tr>
                    </thead>
                    <tbody>
                      {datos.map((producto) => (
                        <tr key={producto.id} style={{ position: "relative" }}>
                          <td>
                            <div style={{ position: "relative" }}>
                              <textarea
                                className="list-products"
                                value={
                                  producto.descripcion || producto.nombre || ""
                                }
                                onChange={(e) => {
                                  handleInputChange(e, producto.id);
                                  setFormDirty(true);
                                }}
                                onBlur={() =>
                                  setTimeout(
                                    () => setFilteredProductos([]),
                                    100
                                  )
                                }
                                placeholder="Escriba para seleccionar un artículo"
                                style={{
                                  width: "600px",
                                  height: "130px",
                                  padding: "8px",
                                  fontSize: "14px",
                                  lineHeight: "1.5",
                                  resize: "vertical",
                                  overflow: "auto",
                                  whiteSpace: "pre-wrap",
                                  fontFamily: "Arial, sans-serif",
                                }}
                              />
                              {activeRow === producto.id &&
                                filteredProductos.length > 0 && (
                                  <ul
                                    style={{
                                      position: "absolute",
                                      top: "105%",
                                      left: 0,
                                      width: "100%",
                                      backgroundColor: "#fff",
                                      border: "1px solid #cecece",
                                      borderRadius: "5px",
                                      zIndex: 10,
                                      maxHeight: "150px",
                                      overflowY: "auto",
                                      listStyle: "none",
                                      padding: "5px",
                                      margin: 0,
                                    }}
                                  >
                                    {filteredProductos.map((prod) => (
                                      <li
                                        key={prod.id}
                                        onMouseDown={() => {
                                          handleInputChange(
                                            { target: { value: prod.nombre } },
                                            producto.id
                                          );
                                          setFormDirty(true);
                                        }}
                                        style={{
                                          padding: "5px",
                                          cursor: "pointer",
                                          backgroundColor: "#f9f9f9",
                                          marginBottom: "2px",
                                        }}
                                      >
                                        {prod.nombre}
                                      </li>
                                    ))}
                                  </ul>
                                )}
                            </div>
                          </td>

                          <td>
                            <input
                              type="text"
                              value={
                                producto.modelo ||
                                producto.numeroDeParteCliente ||
                                ""
                              }
                              onChange={(e) => {
                                const modelo = e.target.value;
                                setDatos((prev) =>
                                  prev.map((item) =>
                                    item.id === producto.id
                                      ? { ...item, modelo }
                                      : item
                                  )
                                );
                                setFormDirty(true);
                              }}
                            />
                          </td>

                          <td id="cantidad-col">
                            <input
                              type="number"
                              inputMode="decimal"
                              step="0.01"
                              min="0"
                              value={producto.cantidad ?? 0} // ← ya no cae a 1
                              onFocus={(e) => e.target.select()} // ← escribir directo
                              onChange={(e) => {
                                const v = e.target.value;
                                const qty =
                                  v === ""
                                    ? 0
                                    : parseFloat(v.replace(",", "."));
                                actualizarCantidad(
                                  producto.id,
                                  isNaN(qty) ? 0 : qty
                                ); // ← decimales
                                setFormDirty(true);
                              }}
                            />
                          </td>

                          <td id="precio-col">
                            <input
                              type="number"
                              inputMode="decimal"
                              step="0.01"
                              min="0"
                              value={producto.tarifa ?? 0}
                              onFocus={(e) => e.target.select()} // ← escribir sin borrar
                              onChange={(e) => {
                                const v = e.target.value;
                                const price =
                                  v === ""
                                    ? 0
                                    : parseFloat(v.replace(",", "."));
                                setDatos((prev) =>
                                  prev.map((item) =>
                                    item.id === producto.id
                                      ? {
                                          ...item,
                                          tarifa: isNaN(price) ? 0 : price,
                                          importe:
                                            (isNaN(price) ? 0 : price) *
                                            (item.cantidad || 0),
                                        }
                                      : item
                                  )
                                );
                                setFormDirty(true);
                              }}
                              onBlur={(e) => {
                                const num = parseFloat(e.target.value) || 0;
                                setDatos((prev) =>
                                  prev.map((item) =>
                                    item.id === producto.id
                                      ? {
                                          ...item,
                                          tarifa: num,
                                          importe: num * (item.cantidad || 0),
                                        }
                                      : item
                                  )
                                );
                              }}
                              style={{
                                width: "100%",
                                textAlign: "right",
                                padding: "8px",
                                fontSize: "14px",
                                borderRadius: "4px",
                              }}
                            />
                          </td>

                          <td id="descuento-col">
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                height: "32px",
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                                overflow: "hidden",
                                width: "100%",
                              }}
                            >
                              <input
                                type="text"
                                inputMode="decimal"
                                value={(producto.descuento ?? "").toString()}
                                onChange={(e) => {
                                  const raw = e.target.value.replace(",", "."); // acepta coma o punto
                                  // permite vacío, ".5", "0.", "10.5", etc.
                                  if (!/^(\d+(\.\d*)?|\.\d*)?$/.test(raw))
                                    return;

                                  setDatos((prev) =>
                                    prev.map((item) => {
                                      if (item.id !== producto.id) return item;

                                      const qty = Number(item.cantidad) || 0;
                                      const price =
                                        parseFloat(item.tarifa) || 0;
                                      const tipo = item.tipoDescuento || "%";
                                      const dnum =
                                        raw === "" || raw === "."
                                          ? 0
                                          : Math.max(parseFloat(raw) || 0, 0);

                                      const importe =
                                        tipo === "%"
                                          ? price * qty * (1 - dnum / 100)
                                          : Math.max(price * qty - dnum, 0);

                                      return {
                                        ...item,
                                        descuento: raw,
                                        importe,
                                      };
                                    })
                                  );
                                  setFormDirty(true);
                                }}
                                onBlur={(e) => {
                                  let d =
                                    parseFloat(
                                      e.target.value.replace(",", ".")
                                    ) || 0;
                                  if (producto.tipoDescuento === "%" && d > 100)
                                    d = 100;
                                  if (d < 0) d = 0;

                                  setDatos((prev) =>
                                    prev.map((item) => {
                                      if (item.id !== producto.id) return item;
                                      const qty = Number(item.cantidad) || 0;
                                      const price =
                                        parseFloat(item.tarifa) || 0;
                                      const tipo = item.tipoDescuento || "%";
                                      const importe =
                                        tipo === "%"
                                          ? price * qty * (1 - d / 100)
                                          : Math.max(price * qty - d, 0);
                                      return { ...item, descuento: d, importe };
                                    })
                                  );
                                }}
                                style={{
                                  flex: 1,
                                  border: "none",
                                  outline: "none",
                                  textAlign: "right",
                                  padding: "0 12px",
                                  fontSize: "16px",
                                  lineHeight: "32px",
                                }}
                              />
                              <div
                                style={{
                                  position: "relative",
                                  width: "50px",
                                  height: "100%",
                                }}
                              >
                                <select
                                  value={producto.tipoDescuento || "%"}
                                  onChange={(e) => {
                                    const tipo = e.target.value;
                                    setDatos((prev) =>
                                      prev.map((item) => {
                                        if (item.id !== producto.id)
                                          return item;
                                        const qty = Number(item.cantidad) || 0;
                                        const price =
                                          parseFloat(item.tarifa) || 0;
                                        const d =
                                          parseFloat(
                                            String(item.descuento).replace(
                                              ",",
                                              "."
                                            )
                                          ) || 0;

                                        const importe =
                                          tipo === "%"
                                            ? price * qty * (1 - d / 100)
                                            : Math.max(price * qty - d, 0);

                                        return {
                                          ...item,
                                          tipoDescuento: tipo,
                                          importe,
                                        };
                                      })
                                    );
                                    setFormDirty(true);
                                  }}
                                  style={{
                                    appearance: "none",
                                    WebkitAppearance: "none",
                                    MozAppearance: "none",
                                    width: "100%",
                                    height: "100%",
                                    border: "none",
                                    outline: "none",
                                    padding: "0 12px",
                                    fontSize: "16px",
                                    lineHeight: "32px",
                                    backgroundColor: "#f4f4f4",
                                    textAlign: "center",
                                    cursor: "pointer",
                                  }}
                                >
                                  <option value="%">%</option>
                                  <option value="$">$</option>
                                </select>
                                <i
                                  style={{
                                    position: "absolute",
                                    right: "8px",
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    pointerEvents: "none",
                                    color: "#666",
                                    fontSize: "12px",
                                  }}
                                />
                              </div>
                            </div>
                          </td>

                          <td
                            id="importe-col"
                            style={{ textAlign: "right", paddingRight: "10px" }}
                          >
                            $
                            {parseFloat(producto.importe || 0).toLocaleString(
                              "en-US",
                              {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }
                            )}
                          </td>

                          {/* Botones de acción */}
                          <div
                            style={{
                              position: "absolute",
                              right: "-75px",
                              top: "50%",
                              transform: "translateY(-50%)",
                              display: "flex",
                            }}
                          >
                            <button
                              type="button"
                              style={{
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                              }}
                            >
                              <i
                                className="bi bi-three-dots-vertical"
                                style={{ fontSize: "1.2rem" }}
                              />
                            </button>
                            <button
                              type="button"
                              style={{
                                background: "none",
                                border: "none",
                                color: "#dc3545",
                                cursor: "pointer",
                              }}
                              onClick={() => eliminar(producto.id)}
                            >
                              <i
                                className="bi bi-x-lg"
                                style={{ fontSize: "1.2rem" }}
                              />
                            </button>
                          </div>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="acciones-tabla">
              <button onClick={añadirFila}>
                <i
                  style={{ color: "#007bff", fontSize: "16px" }}
                  className="bi bi-plus-circle-fill"
                ></i>
                Añadir nueva fila
              </button>
              <button type="button" onClick={() => agregarBulk(datos)}>
                <i
                  style={{ color: "#007bff", fontSize: "16px" }}
                  className="bi bi-plus-circle-fill"
                ></i>
                Agregar artículos a granel
              </button>
            </div>

            <div
              className="data-container"
              style={{
                display: "flex",
                justifyContent: "flex-end",
                paddingRight: "80px",
              }}
            >
              <div className="totales-container">
                {/* Subtotal después de aplicar el descuento */}
                <div className="row">
                  <span className="label">Subtotal</span>
                  <span>
                    $
                    {subtotalConDesc.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </div>

                {/* Descuento en porcentaje */}
                <div className="row descuento">
                  <span className="label">Descuento</span>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      border: "1px solid #ddd",
                      borderRadius: "8px",
                      overflow: "visible",
                      position: "relative",
                      height: 40,
                      minWidth: 180,
                      background: "#fff",
                    }}
                  >
                    <input
                      type="text"
                      inputMode="decimal"
                      value={(descuento ?? "").toString()}
                      onChange={(e) => {
                        const raw = e.target.value.replace(",", ".");
                        if (!/^(\d+(\.\d*)?|\.\d*)?$/.test(raw)) return;
                        setDescuento(raw === "" ? "" : raw);
                        setFormDirty(true);
                      }}
                      onBlur={(e) => {
                        let d =
                          parseFloat(e.target.value.replace(",", ".")) || 0;
                        if (tipoDescuentoGlobal === "%" && d > 100) d = 100;
                        if (
                          tipoDescuentoGlobal === "MXN" &&
                          d > calcularSubtotal()
                        )
                          d = calcularSubtotal();
                        if (d < 0) d = 0;
                        setDescuento(d);
                      }}
                      style={{
                        flex: 1,
                        border: "none",
                        outline: "none",
                        textAlign: "right",
                        padding: "0 12px",
                        fontSize: "16px",
                        lineHeight: "32px",
                        background: "transparent",
                      }}
                    />

                    <MiniSelect
                      value={tipoDescuentoGlobal}
                      options={[
                        { value: "%", label: "%" },
                        { value: "MXN", label: "MXN" },
                      ]}
                      onChange={(next) => {
                        setTipoDescuentoGlobal(next);
                        setDescuento((prev) => {
                          let d = parseFloat(prev) || 0;
                          if (next === "%" && d > 100) d = 100;
                          if (next === "MXN" && d > calcularSubtotal())
                            d = calcularSubtotal();
                          if (d < 0) d = 0;
                          return d;
                        });
                      }}
                    />
                  </div>

                  <span className="discount-percentage">
                    – $
                    {(tipoDescuentoGlobal === "%"
                      ? calcularSubtotal() *
                        ((parseFloat(descuento) || 0) / 100)
                      : Math.min(parseFloat(descuento) || 0, calcularSubtotal())
                    ).toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </div>

                {/* IVA calculado */}
                <div className="row iva">
                  <span className="label">
                    IVA ({(obtenerIVA() * 100).toFixed(0)}%)
                  </span>
                  <span>
                    $
                    {iva.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </div>

                <hr />

                {/* Total final */}
                <div className="row total">
                  <span>Total ({divisa})</span>
                  <span className="total-amount">
                    $
                    {total.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </div>
              </div>
            </div>

            <div
              style={{
                backgroundColor: "#f9f9fb",
                padding: "8px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: "10px",
                width: "94%",
                marginTop: "30px",
                paddingBottom: "30px",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
              }}
            >
              {/* Left Section: Notas del Proveedor */}
              <div
                className="notas-proveedor-container"
                style={{
                  flex: "0 1 58%", // ← ancho base ~58% (se puede 55–62%)
                  maxWidth: 680, // ← tope en px (ajusta a gusto: 640/700)
                  minWidth: 420, // ← evita que colapse en pantallas chicas
                  marginTop: "30px",
                  marginRight: "24px", // ← un poco más de separación del panel derecho
                }}
              >
                <h4>Notas del Proveedor</h4>
                <textarea
                  value={notas}
                  onChange={(e) => {
                    setNotas(e.target.value);
                    setFormDirty(true);
                  }}
                  onFocus={() => setNotasFocus(true)}
                  onBlur={() => setNotasFocus(false)}
                  placeholder="Escribe tus notas aquí..."
                  style={{
                    width: "100%",
                    minHeight: 120,
                    padding: "10px 12px",
                    borderRadius: 6,
                    outline: "none",
                    border: `1px solid ${notasFocus ? "#007bff" : "#ced4da"}`,
                    boxShadow: notasFocus
                      ? "0 0 0 0.2rem rgba(0,123,255,.25)"
                      : "none",
                    transition:
                      "border-color .15s ease-in-out, box-shadow .15s ease-in-out",
                    boxSizing: "border-box",
                  }}
                />
              </div>

              {/* Right Section: File Upload */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: "8px",
                  flex: "0 0 530px",
                  marginTop: "30px",
                }}
              >
                <label style={{ fontSize: "14px" }}>
                  Adjuntar archivo(s) a Estimación
                </label>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    border: "1px dashed #ccc",
                    borderRadius: "5px",
                    overflow: "hidden", // Eliminar cualquier contenido que se salga del borde
                  }}
                >
                  <label
                    style={{
                      padding: "8px 10px",
                      backgroundColor: "#fff",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      cursor: "pointer",
                      whiteSpace: "nowrap", // Evitar que el contenido del botón se ajuste en varias líneas
                    }}
                  >
                    <i style={{ color: "#000" }} className="bi bi-upload"></i>
                    <span
                      style={{
                        fontSize: "14px",
                        color: "#000",
                        fontWeight: "lighter",
                      }}
                    >
                      Cargar archivo
                    </span>
                    <input
                      type="file"
                      style={{ display: "none" }} // Ocultar el input de archivo
                      accept=".csv,.xls,.xlsx,.doc,.docx,.pdf,.jpg,.jpeg,.png,.svg,.txt"
                      onChange={manejarArchivos}
                    />
                  </label>

                  <div
                    style={{
                      padding: "10px",
                      backgroundColor: "#fff",
                      borderLeft: "1px dashed #ccc", // Borde que conecta con el input
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      borderTopRightRadius: "auto", // Bordes redondeados en la esquina derecha
                      borderBottomRightRadius: "auto",
                      minWidth: "20px", // Evitar que el botón se haga demasiado pequeño
                      margin: "0", // Eliminar cualquier margen que agregue espacio extra
                    }}
                  >
                    <i
                      style={{
                        color: "#000",
                        fontSize: "14px", // Ajustar el tamaño del ícono
                      }}
                      className="bi bi-chevron-down"
                    ></i>
                  </div>
                </div>

                <p style={{ fontSize: "12px", color: "#888" }}>
                  Puedes cargar un máximo de 10 archivos, cada uno de 10 MB
                </p>

                {cargando && (
                  <p style={{ fontSize: "14px", color: "#888" }}>
                    Cargando archivo...
                  </p>
                )}

                {archivos.length > 0 && (
                  <div style={{ width: "100%" }}>
                    <strong>Archivos cargados:</strong>
                    <ul
                      style={{
                        listStyle: "none",
                        padding: 0,
                        marginTop: "10px",
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                      }}
                    >
                      {archivos.map(({ nombre, url }) => {
                        const ext = nombre.split(".").pop().toLowerCase();
                        const { icon, color } = obtenerIcono(ext);

                        return (
                          <li
                            key={nombre}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "5px",
                              padding: "10px",
                              border: "1px solid #ddd",
                              borderRadius: "5px",
                              boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                              backgroundColor: "#f9f9fb",
                            }}
                          >
                            <i
                              className={`bi ${icon}`}
                              style={{
                                color,
                                fontSize: "1.5rem",
                                flexShrink: 0,
                              }}
                            />
                            <span style={{ flexGrow: 1 }}>{nombre}</span>

                            {/* Descargar */}
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                descargarArchivo(url, nombre);
                              }}
                              style={{
                                color: "#007bff",
                                background: "none",
                                border: "none",
                                fontWeight: "bold",
                                cursor: "pointer",
                              }}
                            >
                              <i
                                className="bi bi-download"
                                style={{ fontSize: "20px" }}
                              />
                            </button>

                            {/* Eliminar */}
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleEliminarAdjunto(nombre); // ← aquí
                              }}
                              style={{
                                border: "none",
                                background: "transparent",
                                color: "#dc3545",
                                cursor: "pointer",
                              }}
                            >
                              <i
                                className="bi bi-trash"
                                style={{ fontSize: "20px" }}
                              />
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Grupo de botones: Guardar como borrador, Guardar y enviar, Cancelar */}
          <div className="cotizacion-toolbar">
            <div className="btn-group">
              <button
                id="guardar-enviar"
                className="btn btn-primary"
                type="button"
                onClick={guardar}
              >
                {modoEdicion ? "Actualizar" : "Guardar y enviar"}
              </button>
              <button
                id="cancelar"
                className="btn btn-secondary"
                type="button"
                onClick={handleCancel}
              >
                Cancelar
              </button>
            </div>
          </div>
        </form>

        {mostrarTabla && (
          <TablaOrdenes ordenes={ordenes} setOrdenes={setOrdenes} />
        )}
      </div>

      <NumeroConfigModalUI />
    </>
  );
}

export default OrdenForm;
